/**
 * Core Concepts overview — removed from sidebar, kept as a redirect target.
 *
 * Provides a brief overview of the fundamental building blocks
 * and links to dedicated pages for each concept.
 */

export const coreConceptsContent = `
m8tes is built around a few core concepts that work together.

## Teammates

A **teammate** is a reusable persona with a name, instructions, and tools. Configure once, run many times.

\`\`\`python
from m8tes import M8tes

client = M8tes()

bot = client.teammates.create(
    name="Support Bot",
    tools=["slack", "linear"],
    instructions="Handle customer tickets efficiently"
)
\`\`\`

[Full Teammates guide](/docs/teammates)

## Tasks

A **task** defines what a teammate should do. Tasks can be **reusable** — created explicitly with a name and triggers (schedule, webhook, email) — or **ad-hoc**, created automatically when you send a one-off message. A task can have multiple runs.

\`\`\`python
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Generate weekly support metrics",
    name="Weekly Report"
)
\`\`\`

[Full Tasks guide](/docs/tasks)

## Runs

A **run** is a single execution. Every run belongs to a task — either a reusable task you created, or one the system creates automatically for ad-hoc requests. Runs stream events in real-time and persist results. Follow up with \`runs.reply()\` for multi-turn conversations.

\`\`\`python
run = client.runs.create(teammate_id=bot.id, message="Close open tickets", stream=False)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

[Full Runs guide](/docs/runs)

## Tools

Tools are the capabilities teammates can use — Gmail, Slack, Google Ads, and more.

\`\`\`python
page = client.apps.list()
for app in page.data:
    print(app.name, "connected" if app.connected else "not connected")
\`\`\`

[Full Tools guide](/docs/skills)

## How They Fit Together

\`\`\`
Teammate → Task → Run
\`\`\`

- **Teammates** hold the persona and tools
- **Tasks** hold the instructions (reusable or ad-hoc)
- **Runs** hold the execution results

Every run has exactly one task. Every task belongs to exactly one teammate. A task can have many runs.

## How It Works

\`\`\`
Your Code → v2 API → Agent Runtime → Claude → MCP Tools
\`\`\`

1. Your code calls the SDK (or REST API directly)
2. The API authenticates, creates resources, and starts execution
3. The runtime streams Claude's reasoning and tool calls back in real-time

## Multi-Tenancy

Use \`user_id\` to isolate data per end-user when building products on top of m8tes:

\`\`\`python
bot = client.teammates.create(name="Bot", user_id="customer_123")
run = client.runs.create(teammate_id=bot.id, message="...", user_id="customer_123", stream=False)
\`\`\`

[Full Users guide](/docs/users)
`;
